import { useCallback, useEffect, useState } from 'react';
import { Images, Trash2, Upload, ChevronDown, ChevronUp } from 'lucide-react';
import type { AssetItem, AssetFolder } from '@yp/types';
import { useAuth } from '../context/AuthContext';
import { getAssets, deleteAsset } from '../lib/api';
import ImagePickerModal from '../components/ImagePickerModal';

const FOLDERS: AssetFolder[] = ['members', 'news', 'benefits', 'discography'];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AssetsPage() {
  const { accessToken } = useAuth();
  const [activeFolder, setActiveFolder] = useState<AssetFolder>('members');
  const [assets,       setAssets]       = useState<AssetItem[]>([]);
  const [loading,      setLoading]      = useState(false);
  const [expandedId,   setExpandedId]   = useState<string | null>(null);
  const [deletingId,   setDeletingId]   = useState<string | null>(null);
  const [uploadOpen,   setUploadOpen]   = useState(false);

  const loadAssets = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try { setAssets(await getAssets(accessToken, activeFolder)); }
    finally { setLoading(false); }
  }, [accessToken, activeFolder]);

  useEffect(() => { loadAssets(); }, [loadAssets]);

  async function handleDelete(id: string) {
    if (!accessToken) return;
    setDeletingId(id);
    try {
      await deleteAsset(accessToken, id);
      setAssets((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Gagal menghapus asset');
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-100">Assets</h1>
        <button
          onClick={() => setUploadOpen(true)}
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
          style={{ backgroundColor: 'var(--yp-pink)' }}
        >
          <Upload size={15} />
          Upload
        </button>
      </div>

      {/* Folder nav */}
      <div className="mb-5 flex gap-2">
        {FOLDERS.map((folder) => (
          <button
            key={folder}
            onClick={() => setActiveFolder(folder)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
              activeFolder === folder
                ? 'bg-pink-500/20 text-pink-400'
                : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
            }`}
          >
            {folder}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-pink-500 border-t-transparent" />
        </div>
      ) : assets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <Images size={36} className="mb-3 opacity-30" />
          <p className="text-sm">Belum ada assets di folder ini.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {assets.map((asset) => (
            <div key={asset.id} className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
              <div className="aspect-square overflow-hidden bg-black/10">
                <img src={asset.url} alt={asset.name} className="h-full w-full object-cover" />
              </div>
              <div className="p-3">
                <p className="truncate text-xs font-medium text-gray-300" title={asset.name}>{asset.name}</p>
                <p className="mt-0.5 text-[10px] text-gray-500">{asset.width}×{asset.height} · {formatBytes(asset.size)}</p>
                <div className="mt-2 flex items-center justify-between">
                  {asset.usage_count > 0 ? (
                    <button
                      onClick={() => setExpandedId(expandedId === asset.id ? null : asset.id)}
                      className="flex items-center gap-1 rounded-full bg-yellow-500/10 px-2 py-0.5 text-[10px] text-yellow-400 hover:bg-yellow-500/20 transition-colors"
                    >
                      Dipakai {asset.usage_count}×
                      {expandedId === asset.id ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                    </button>
                  ) : (
                    <span className="text-[10px] text-gray-600">Tidak dipakai</span>
                  )}
                  <button
                    onClick={() => handleDelete(asset.id)}
                    disabled={asset.usage_count > 0 || deletingId === asset.id}
                    title={asset.usage_count > 0 ? 'Hapus referensi terlebih dahulu' : 'Hapus asset'}
                    className="rounded p-1 text-gray-500 transition-colors hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                {expandedId === asset.id && (
                  <div className="mt-2 space-y-1 border-t border-[var(--color-border)] pt-2">
                    {asset.usages.map((u, i) => (
                      <p key={i} className="text-[10px] capitalize text-gray-400">{u.feature}: {u.label}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ImagePickerModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSelect={(_url) => { setUploadOpen(false); loadAssets(); }}
        defaultFolder={activeFolder}
        initialTab="upload"
      />
    </div>
  );
}
